import {
  Button, Space, Tooltip, Tag, message, Breadcrumb,
  Input, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, SearchOutlined, EyeOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { childApi } from '../../../api/child.api';
import { schoolApi } from '../../../api/school.api';
import DataTable from '../../../components/common/DataTable';
import ChildForm from '../components/ChildForm';
import ChildEventsDrawer from '../components/ChildEventsDrawer';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';
import { usePermission } from '../../../hooks/usePermission';
import { getApiErrorMessage } from '../../../utils/helpers';

const STANDARD_OPTIONS = [
  'Nursery', 'LKG', 'UKG',
  '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th', '9th', '10th',
  '11th', '12th',
];

const ChildList = () => {
  const [searchParams]   = useSearchParams();
  const fromSchool       = searchParams.get('school') || null;

  const [formOpen,       setFormOpen]       = useState(false);
  const [editRecord,     setEditRecord]     = useState(null);
  const [viewChild,      setViewChild]      = useState(null);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [searchInput,     setSearchInput]     = useState('');
  const [schoolFilter,    setSchoolFilter]    = useState(fromSchool || 'all');
  const [standardFilter,  setStandardFilter]  = useState('all');
  const [sectionFilter,   setSectionFilter]   = useState('all');
  const [pagination,    setPagination]    = useState({ page: 1, limit: 10 });
  const [sort,          setSort]          = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate    = useNavigate();
  const { isAdmin, isDoctor, isSchoolStaff } = usePermission();
  const canCreateChild = isAdmin || isDoctor || isSchoolStaff;

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: schoolsData } = useQuery({
    queryKey: ['schools-all'],
    queryFn:  () => schoolApi.getAll({ limit: 200 }),
    enabled:  isAdmin,
  });
  const schoolOptions = (schoolsData?.data || []).map((s) => ({ value: s.id || s._id, label: s.name }));

  const { data, isLoading } = useQuery({
    queryKey: ['children', debouncedSearch, sort, pagination, schoolFilter, standardFilter, sectionFilter],
    queryFn:  () => childApi.getAll({
      search: debouncedSearch, ...sort, ...pagination,
      ...(schoolFilter && schoolFilter !== 'all' ? { school: schoolFilter } : {}),
      ...(standardFilter && standardFilter !== 'all' ? { standard: standardFilter } : {}),
      ...(sectionFilter && sectionFilter !== 'all'  ? { section:  sectionFilter }  : {}),
    }),
  });

  const createMutation = useMutation({
    mutationFn: childApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      message.success('Child added');
      setFormOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => childApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      message.success('Child updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => {
      if (err?.errorCode !== 'PARENT_ACTION_REQUIRED') { message.error(getApiErrorMessage(err)); }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: childApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      message.success('Child deleted');
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const handleFormSubmit = (values) => {
    if (editRecord) { return updateMutation.mutateAsync({ id: editRecord.id, data: values }); }
    return createMutation.mutate(values);
  };

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
    if (extra?.action !== 'sort') return;
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSort(nextSorter?.order
      ? {
          sortBy:    nextSorter.columnKey,
          sortOrder: nextSorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const getSortOrder = (field) =>
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null;

  const allChildren = data?.data || [];
  const meta        = data?.meta || {};
  const GENDER_COLOR = { male: 'blue', female: 'pink', other: 'purple' };

  const sectionOptions = useMemo(() => {
    const seen = new Set();
    allChildren.forEach(c => { if (c.section) seen.add(c.section); });
    return [...seen].sort();
  }, [allChildren]);

  const columns = [
    {
      title: 'Child Name', dataIndex: 'name', key: 'name',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('name'),
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Gender', dataIndex: 'gender', key: 'gender',
      width: 100, sorter: true, sortOrder: getSortOrder('gender'),
      render: (v) => (
        <Tag color={GENDER_COLOR[v] || 'default'} style={{ borderRadius: 9999, fontSize: 11 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Age', dataIndex: 'age', key: 'dateOfBirth',
      width: 90, sorter: true, sortOrder: getSortOrder('dateOfBirth'),
      render: (v) => v ? `${v} yrs` : '-',
    },
    {
      title: 'Standard', dataIndex: 'standard', key: 'standard',
      width: 100,
      render: (v, r) => v ? `${v}${r.section ? ' - ' + r.section : ''}` : '-',
    },
    {
      title: 'School', key: 'schoolName',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('schoolName'),
      render: (_, r) => r.school?.name ? <Tooltip title={r.school.name}>{r.school.name}</Tooltip> : '-',
    },
    {
      title: 'Parent Name', key: 'parentName',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('parentName'),
      render: (_, r) => r.parent?.name ? <Tooltip title={r.parent.name}>{r.parent.name}</Tooltip> : '-',
    },
    {
      title: 'Parent Phone', key: 'parentPhone',
      width: 140, sorter: true, sortOrder: getSortOrder('parentPhone'),
      render: (_, r) => r.parent?.phone || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 110,
      render: (_, record) => (
        <Space size={6}>
          {!fromSchool && (
            <Tooltip title="View Child Events">
              <Button
                shape="circle"
                size="small"
                icon={<EyeOutlined />}
                style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                onClick={() => { setViewChild(record); setViewDrawerOpen(true); }}
              />
            </Tooltip>
          )}
          {canCreateChild && (
            <Tooltip title="Edit">
              <Button
                shape="circle"
                size="small"
                icon={<EditOutlined />}
                style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                onClick={() => { setEditRecord(record); setFormOpen(true); }}
              />
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                size="small"
                icon={<DeleteOutlined />}
                danger
                style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
                onClick={() => showDeleteConfirm({
                  content: 'Are You Sure You Want To Delete this child record?',
                  onOk: () => deleteMutation.mutate(record.id),
                })}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={fromSchool ? [
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/schools')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Schools</span> },
          { title: 'Children' },
        ] : [
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Children / Patients' },
        ]}
      />

      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
        flexWrap:     'wrap',
      }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 12px',
            borderRadius: 9999,
            background: 'var(--wl-primary-pale)',
            border: '1px solid var(--wl-primary-light)',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--wl-primary)',
          }}
        >
          <TeamOutlined style={{ color: 'var(--wl-primary)' }} />
          <span>Total:</span>
          <span
            style={{
              background: 'var(--wl-primary)',
              color: '#ffffff',
              borderRadius: 9999,
              padding: '1px 10px',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {meta.total ?? 0}
          </span>
        </div>

        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by child name, parent name, mobile..."
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, minWidth: 180, maxWidth: 280, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        />

        {isAdmin && (
          <Select
            placeholder="School"
            value={schoolFilter}
            onChange={(val) => {
              setSchoolFilter(val);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            showSearch
            optionFilterProp="children"
            size="middle"
            style={{ width: 200 }}
          >
            <Select.Option key="all" value="all">School: All</Select.Option>
            {schoolOptions.map((s) => (
              <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>
            ))}
          </Select>
        )}

        <Select
          placeholder="Standard"
          value={standardFilter}
          onChange={(val) => {
            setStandardFilter(val);
            setSectionFilter('all');
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          size="middle"
          style={{ width: 150 }}
        >
          <Select.Option key="all" value="all">Standard: All</Select.Option>
          {STANDARD_OPTIONS.map((s) => (
            <Select.Option key={s} value={s}>{s}</Select.Option>
          ))}
        </Select>

        <Select
          placeholder="Section"
          value={sectionFilter}
          onChange={(val) => {
            setSectionFilter(val);
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          size="middle"
          style={{ width: 140 }}
        >
          <Select.Option key="all" value="all">Section: All</Select.Option>
          {sectionOptions.map((s) => (
            <Select.Option key={s} value={s}>{s}</Select.Option>
          ))}
        </Select>

        {canCreateChild && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginLeft: 'auto' }}
            onClick={() => { setEditRecord(null); setFormOpen(true); }}
          >
            Add Child
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        dataSource={allChildren}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 800 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
          onChange: (page, limit) => setPagination({ page, limit }),
        }}
        onChange={handleTableChange}
      />

      <ChildForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <ChildEventsDrawer
        open={viewDrawerOpen}
        onClose={() => { setViewDrawerOpen(false); setViewChild(null); }}
        child={viewChild}
      />

    </div>
  );
};

export default ChildList;
